import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import merge from 'lodash/merge';
import {
	KEY_FIELDKEY,
	ISSUEID_FIELDKEY,
} from '@atlassian/jira-polaris-domain-field/src/field/constants.tsx';
import type { LocalIssueId } from '@atlassian/jira-polaris-domain-idea/src/idea/types.tsx';
import type { RemoteIssue } from '@atlassian/jira-polaris-remote-issue/src/controllers/crud/types.tsx';
import { getMetaFromJiraSearchIssue } from '@atlassian/jira-polaris-remote-issue/src/controllers/util/index.tsx';
import type { IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { FIELD_TYPES } from '@atlassian/jira-polaris-domain-field/src/field-types/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	PolarisInsightsByIssueId,
	PolarisPlayContributionsByIssueId,
} from '../../../project/types.tsx';
import { EMPTY_PROPERTIES } from '../../constants.tsx';
import { getFieldMappings, type FieldMappings } from '../../selectors/fields.tsx';
import type { State, PropertyMaps, Props, ExternalIssueDataMap } from '../../types.tsx';
import { generateLocalIssueId } from '../../utils/local-id.tsx';
import { transformIssueLinkData } from '../common/issue-links/index.tsx';
import { sortIdsByRank, sortIssuesByRank } from '../common/issue-rank/index.tsx';

export const preserveExistingPlays = (
	plays: PolarisPlayContributionsByIssueId,
	existingProperties: PropertyMaps,
	issueMap: Record<LocalIssueId, IssueId>,
) => {
	const finalPlays = {
		...plays,
	};

	for (const [playId, values] of Object.entries(existingProperties.plays)) {
		for (const localIssueId of Object.keys(values)) {
			const jiraIssueId = Object.keys(issueMap).find((key) => issueMap[key] === localIssueId);

			if (jiraIssueId) {
				finalPlays[playId][jiraIssueId] = existingProperties.plays[playId][localIssueId];
			}
		}
	}

	return finalPlays;
};

export const preserveExistingInsights = (
	insights: PolarisInsightsByIssueId,
	existingProperties: PropertyMaps,
	issueMap: Record<LocalIssueId, IssueId>,
) => {
	const finalInsights = {
		...insights,
	};

	for (const localIssueId of Object.keys(issueMap)) {
		const jiraIssueId = issueMap[localIssueId];
		finalInsights[localIssueId] = existingProperties.insights[jiraIssueId];
	}

	return finalInsights;
};

export const isIssueAlreadyLoaded = (
	issueId: IssueId,
	mapping: Record<LocalIssueId, IssueId>,
	fieldMappings: FieldMappings<unknown>,
	existingState: State,
) => {
	if (mapping[issueId] === undefined) {
		return false;
	}

	return (
		fieldMappings[KEY_FIELDKEY].getAllValues(existingState, undefined)[mapping[issueId]] !==
		undefined
	);
};

type TransformIssueResponseOldArgs = {
	remoteIssues: RemoteIssue[];
	primaryIssueList?: IssueId[] | undefined;
	insights: PolarisInsightsByIssueId | undefined;
	plays: PolarisPlayContributionsByIssueId | undefined;
	existingState: State;
	existingMapping?: Record<IssueId, LocalIssueId>;
	props: Props;
};

// TODO: remove when cleaning up `jpd_open-issues-from-diff-container-in-sidebar`
export const transformIssueResponseOld = (args: TransformIssueResponseOldArgs) => {
	const {
		remoteIssues,
		primaryIssueList,
		insights,
		plays,
		existingState,
		existingMapping = {},
		props,
	} = args;
	const { polarisIssueLinkType, hiddenIssueLinkTypes, rankField } = props;
	const fieldMappings = getFieldMappings(existingState, props);

	const ids: Array<LocalIssueId> = [];
	const connectionIssueIds = new Set<LocalIssueId>([]);

	const properties = cloneDeep(EMPTY_PROPERTIES);

	let externalIssueData: ExternalIssueDataMap = {};

	const issues = fg('jpd_open-issues-from-diff-container-in-sidebar')
		? remoteIssues
		: sortIssuesByRank({ issues: remoteIssues, rankField });

	issues.forEach((issue) => {
		const isIssueLoaded = isIssueAlreadyLoaded(
			issue.id,
			existingMapping,
			fieldMappings,
			existingState,
		);

		// reuse the existing local issue id if it already exists
		// this can be the case when loading a single idea in a new tab
		const id = existingMapping[issue.id] || generateLocalIssueId();

		if (existingMapping[issue.id] === undefined) {
			existingMapping[issue.id] = id;
		}

		if (primaryIssueList === undefined || primaryIssueList.includes(issue.id)) {
			ids.push(id);
		} else {
			connectionIssueIds.add(id);
		}

		// we do not want to override the field values of the already loaded issue (if any)
		// because some fields which are already loaded (e.g. description) would have an
		// undefined value as they are not present
		if (!isIssueLoaded) {
			forEach(fieldMappings, (mapping) => {
				// do not attempt to map dynamic fields from jira values
				if (mapping.field?.formula !== undefined) {
					return;
				}

				const fieldValue = mapping.getValueFromJiraIssue(issue);

				mapping.setMutable(properties, id, fieldValue !== null ? fieldValue : undefined);
			});
		}

		// handle lexorank
		if (rankField !== undefined) {
			properties.lexoRank[id] = issue.fields[rankField];
		}

		if (insights !== undefined && Array.isArray(insights[issue.id])) {
			properties.insights[id] = insights[issue.id];
		}

		properties.issueMetadata[id] = getMetaFromJiraSearchIssue(
			issue,
			polarisIssueLinkType,
			hiddenIssueLinkTypes,
		);

		const issueLinkData = transformIssueLinkData(polarisIssueLinkType, hiddenIssueLinkTypes, issue);
		properties.linkedDeliveryIssues[id] = issueLinkData.deliveryIssueIds;
		externalIssueData = merge(externalIssueData, issueLinkData.externalIssueData);
	});

	if (plays) {
		for (const [, play] of Object.entries(plays)) {
			for (const issueId of Object.keys(play)) {
				const localIssueId = existingMapping[issueId];

				if (localIssueId) {
					if (properties.number[ISSUEID_FIELDKEY] === undefined) {
						properties.number[ISSUEID_FIELDKEY] = {};
					}

					fieldMappings[ISSUEID_FIELDKEY].setMutable(
						properties,
						localIssueId,
						Number.parseInt(issueId, 10),
					);
				}
			}
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const issuePlaysWithContributions = Object.keys(plays).reduce<Record<string, any>>(
			(acc, key) => {
				const play = plays[key];

				for (const issueId of Object.keys(play)) {
					const localIssueId = existingMapping[issueId];

					if (Array.isArray(play[issueId])) {
						if (acc[key] === undefined) {
							acc[key] = {};
						}
						acc[key][localIssueId] = play[issueId];
					}
				}

				return acc;
			},
			{},
		);

		properties.plays = merge(properties.plays, issuePlaysWithContributions);
	}

	return {
		ids,
		properties,
		externalIssueData,
		connectionIssueIds: Array.from(connectionIssueIds),
	};
};

type TransformIssueResponseArgs = {
	remoteIssues: RemoteIssue[];
	primaryIssueList?: IssueId[] | undefined;
	insights: PolarisInsightsByIssueId | undefined;
	plays: PolarisPlayContributionsByIssueId | undefined;
	existingState: State;
	existingMapping?: Record<IssueId, LocalIssueId>;
	props: Props;
	mergeConnectionFieldValues: boolean;
	skipMetaIfAlreadyLoaded: boolean;
};

export const transformIssueResponse = (args: TransformIssueResponseArgs) => {
	const {
		remoteIssues,
		primaryIssueList,
		insights,
		plays,
		existingState,
		existingMapping = {},
		props,
		mergeConnectionFieldValues,
		skipMetaIfAlreadyLoaded,
	} = args;
	const { polarisIssueLinkType, hiddenIssueLinkTypes, rankField } = props;
	const fieldMappings = getFieldMappings(existingState, props);

	const ids = new Set<LocalIssueId>(existingState.ids);
	const connectionIssueIds = new Set<LocalIssueId>(existingState.connectionIssueIds);

	const properties = cloneDeep(existingState.properties);

	let externalIssueData = cloneDeep(existingState.externalIssueData);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const issues = sortIssuesByRank({ issues: remoteIssues, rankField }) as RemoteIssue[];

	issues.forEach((issue) => {
		const isIssueLoaded = isIssueAlreadyLoaded(
			issue.id,
			existingMapping,
			fieldMappings,
			existingState,
		);

		const id = existingMapping[issue.id] || generateLocalIssueId();

		if (existingMapping[issue.id] === undefined) {
			existingMapping[issue.id] = id;
		}

		if (primaryIssueList === undefined || primaryIssueList.includes(issue.id)) {
			ids.add(id);
		} else {
			connectionIssueIds.add(id);
		}

		// If issue is already loaded, only overwrite the properties that are part of the issue response
		const mappingsToIterateOver = isIssueLoaded
			? Object.keys(issue.fields)
					.map((fieldKey) => fieldMappings[fieldKey])
					.filter(Boolean)
			: Object.values(fieldMappings);

		forEach(mappingsToIterateOver, (mapping) => {
			// do not attempt to map dynamic fields from jira values
			if (mapping.field?.formula !== undefined) {
				return;
			}

			let fieldValue: unknown;
			if (mapping.field?.type === FIELD_TYPES.CONNECTION && mergeConnectionFieldValues) {
				const additionalConnections = mapping.getValueFromJiraIssue(issue);
				const existingConnections = mapping.valueAccessor(existingState, props, id);
				const joinedConnectionIds = [...existingConnections, ...(additionalConnections ?? [])].map(
					({ id: connectionId }) => connectionId,
				);
				fieldValue = Array.from(new Set(joinedConnectionIds)).map((connectionId) => ({
					id: connectionId,
				}));
			} else {
				fieldValue = mapping.getValueFromJiraIssue(issue);
			}

			mapping.setMutable(properties, id, fieldValue !== null ? fieldValue : undefined);
		});

		// handle lexorank
		if (rankField !== undefined) {
			properties.lexoRank[id] = issue.fields[rankField];
		}

		if (insights !== undefined && Array.isArray(insights[issue.id])) {
			properties.insights[id] = insights[issue.id];
		}

		const shouldProcessIssueMetadata =
			!fg('jpd_open-issues-from-diff-container-in-sidebar') ||
			!isIssueLoaded ||
			!skipMetaIfAlreadyLoaded;

		if (shouldProcessIssueMetadata) {
			properties.issueMetadata[id] = getMetaFromJiraSearchIssue(
				issue,
				polarisIssueLinkType,
				hiddenIssueLinkTypes,
			);

			const issueLinkData = transformIssueLinkData(
				polarisIssueLinkType,
				hiddenIssueLinkTypes,
				issue,
			);
			properties.linkedDeliveryIssues[id] = issueLinkData.deliveryIssueIds;
			externalIssueData = merge(externalIssueData, issueLinkData.externalIssueData);
		}
	});

	if (plays) {
		for (const [, play] of Object.entries(plays)) {
			for (const issueId of Object.keys(play)) {
				const localIssueId = existingMapping[issueId];

				if (localIssueId) {
					if (properties.number[ISSUEID_FIELDKEY] === undefined) {
						properties.number[ISSUEID_FIELDKEY] = {};
					}

					fieldMappings[ISSUEID_FIELDKEY].setMutable(
						properties,
						localIssueId,
						Number.parseInt(issueId, 10),
					);
				}
			}
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const issuePlaysWithContributions = Object.keys(plays).reduce<Record<string, any>>(
			(acc, key) => {
				const play = plays[key];

				for (const issueId of Object.keys(play)) {
					const localIssueId = existingMapping[issueId];

					if (Array.isArray(play[issueId])) {
						if (acc[key] === undefined) {
							acc[key] = {};
						}
						acc[key][localIssueId] = play[issueId];
					}
				}

				return acc;
			},
			{},
		);

		properties.plays = merge(properties.plays, issuePlaysWithContributions);
	}

	const sortedIds =
		rankField && fg('jpd_open-issues-from-diff-container-in-sidebar')
			? sortIdsByRank({ ids: Array.from(ids), lexoRankProperties: properties.lexoRank })
			: Array.from(ids);
	const sortedConnectionIds =
		rankField && fg('jpd_open-issues-from-diff-container-in-sidebar')
			? sortIdsByRank({
					ids: Array.from(connectionIssueIds),
					lexoRankProperties: properties.lexoRank,
				})
			: Array.from(connectionIssueIds);

	return {
		ids: sortedIds,
		properties,
		externalIssueData,
		connectionIssueIds: sortedConnectionIds,
	};
};
